/**
 * AIModeration — admin queue that lists forum posts the AI Ambassador
 * flagged as misleading. Admin can jump into the flagged topic, dismiss
 * the flag (post is fine), or mark it "actioned" once they've deleted /
 * edited the offending content.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import ToolSelector from '../../components/ToolSelector';
import ConfidenceHeatmap from './ConfidenceHeatmap';
import ReportsQueue from './ReportsQueue';
import CommunityHealth from './CommunityHealth';
import { Button } from '../../components/ui/button';
import {
  ShieldAlert, ArrowLeft, ExternalLink, CheckCircle2, X, Filter, RefreshCw, AlertTriangle,
  Bot, MessageSquare, TrendingUp, Flame, HelpCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const rel = (iso) => {
  if (!iso) return '';
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return ''; }
};

const VERDICT_STYLE = {
  misleading: 'bg-rose-500/15 text-rose-700 border-rose-500/30',
  partially_accurate: 'bg-amber-500/15 text-amber-700 border-amber-500/30',
  unverified: 'bg-slate-500/15 text-slate-700 border-slate-500/30',
  accurate: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
};

export default function AIModeration() {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [flags, setFlags] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([
        axios.get(`${API}/admin/forum/flags?status=${statusFilter}`),
        axios.get(`${API}/admin/forum/ambassador-stats?days=7`),
      ]);
      setFlags(f.data.flags || []);
      setStats(s.data);
    } catch {
      toast.error('Failed to load moderation dashboard');
      setFlags([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, API]);

  const resolve = async (flag, action) => {
    try {
      await axios.post(`${API}/admin/forum/flags/${flag.id}/resolve`, { action });
      toast.success(action === 'dismiss' ? 'Flag dismissed' : 'Marked as actioned');
      load();
    } catch { toast.error('Could not update flag'); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-foreground mb-3">
          <ArrowLeft className="w-3 h-3" /> Admin
        </Link>
        <div className="flex items-center gap-3 mb-1">
          <div className="h-10 w-10 rounded-full bg-rose-500/15 text-rose-600 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div>
            <div className="eyebrow text-rose-600">AI Moderation</div>
            <h1 className="font-heading text-2xl font-extrabold text-foreground">Community flags</h1>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
          Posts the AI Research Ambassador flagged as potentially misleading against Freeze Dry Academy&apos;s reference knowledge.
          Every flagged post shows a public &ldquo;Under Review&rdquo; badge until you resolve it.
        </p>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="inline-flex rounded-full border border-border overflow-hidden text-[11px] font-bold" role="tablist" data-testid="flag-status-tabs">
            {['open', 'dismissed', 'actioned', 'all'].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setStatusFilter(k)}
                data-testid={`flag-tab-${k}`}
                className={`px-3 h-8 capitalize transition-colors inline-flex items-center gap-1 ${
                  statusFilter === k ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Filter className="w-3 h-3" /> {k}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={load} className="rounded-full ml-auto" data-testid="flag-refresh">
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* AI ambassador usage stats — last 7 days */}
        {stats && (
          <section className="pro-card p-4 mb-5" data-testid="ambassador-stats">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-full bg-primary/15 text-primary flex items-center justify-center">
                <Bot className="w-4 h-4" />
              </div>
              <div>
                <div className="eyebrow text-primary">Ambassador usage · last {stats.days} days</div>
                <div className="text-xs text-muted-foreground">What members are asking the AI + which threads they open next.</div>
              </div>
              <div className="ml-auto grid grid-cols-2 gap-2 text-center">
                <div className="rounded-lg bg-muted px-3 py-1.5">
                  <div className="text-lg font-extrabold text-foreground leading-none">{stats.total_questions}</div>
                  <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">Questions</div>
                </div>
                <div className="rounded-lg bg-muted px-3 py-1.5">
                  <div className="text-lg font-extrabold text-foreground leading-none">{stats.total_clicks}</div>
                  <div className="text-[9px] uppercase tracking-widest font-bold text-muted-foreground">Citation clicks</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5 inline-flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Top clicked threads
                </div>
                <div className="space-y-1" data-testid="ambassador-top-topics">
                  {stats.top_topics.length === 0 && (
                    <div className="text-xs text-muted-foreground">No citation clicks yet.</div>
                  )}
                  {stats.top_topics.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => navigate(`/community/t/${t.id}`)}
                      data-testid={`stats-top-topic-${t.id}`}
                      className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted transition-colors text-left"
                    >
                      <MessageSquare className="w-3 h-3 text-primary flex-shrink-0" />
                      <span className="text-xs font-semibold text-foreground truncate flex-1">{t.title}</span>
                      {t.is_archived && (
                        <span className="text-[9px] uppercase tracking-widest font-bold bg-slate-500/15 text-slate-600 rounded px-1.5 py-0.5">Archived</span>
                      )}
                      <span className="text-[10px] font-bold text-muted-foreground flex-shrink-0">{t.clicks} clicks</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5 inline-flex items-center gap-1">
                  <MessageSquare className="w-3 h-3" /> Recent questions
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto" data-testid="ambassador-recent-questions">
                  {stats.recent_questions.length === 0 && (
                    <div className="text-xs text-muted-foreground">No questions yet.</div>
                  )}
                  {stats.recent_questions.map((q) => (
                    <div
                      key={q.id}
                      className="rounded-lg border border-border px-2 py-1.5"
                      data-testid={`stats-question-${q.id}`}
                    >
                      <div className="text-xs text-foreground line-clamp-2">{q.question}</div>
                      <div className="mt-0.5 text-[10px] text-muted-foreground">
                        {q.citation_clicks} clicks · {(q.citation_ids || []).length} citations
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}
        {/* Community Health rollup — moderation pulse at a glance */}
        <CommunityHealth />

        {/* User-submitted community reports queue */}
        <ReportsQueue />

        {/* Confidence heatmap — which lessons are producing low self-ratings */}
        <ConfidenceHeatmap />


        <div className="pro-card divide-y divide-border" data-testid="flag-list">
          {flags.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
              Nothing to review — the community is looking clean.
            </div>
          )}
          {flags.map((flag) => (
            <div key={flag.id} className="p-4 space-y-2" data-testid={`flag-${flag.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 rounded-full border font-bold uppercase tracking-widest text-[10px] px-2 py-0.5 ${VERDICT_STYLE[flag.verdict] || VERDICT_STYLE.unverified}`}>
                  <AlertTriangle className="w-3 h-3" /> {flag.verdict.replace('_', ' ')}
                </span>
                <span className="text-[11px] font-bold text-muted-foreground">
                  {Math.round((flag.confidence || 0) * 100)}% confidence
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {flag.target_type} · {rel(flag.created_at)}
                </span>
                <span className={`ml-auto text-[10px] font-bold uppercase tracking-widest rounded px-2 py-0.5 ${
                  flag.status === 'open' ? 'bg-rose-500/10 text-rose-700' : 'bg-muted text-muted-foreground'
                }`}>
                  {flag.status}
                </span>
              </div>
              <div>
                <div className="text-sm font-heading font-bold text-foreground">
                  {flag.topic?.title || '(topic missing)'}
                </div>
                <div className="text-xs text-muted-foreground">
                  by {flag.author?.name || 'Unknown'}
                </div>
              </div>
              <div className="text-sm text-foreground/85 leading-relaxed">{flag.reason}</div>
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                <Button size="sm" variant="outline" className="rounded-full"
                  onClick={() => navigate(`/community/t/${flag.topic_id}`)}
                  data-testid={`flag-open-${flag.id}`}
                >
                  <ExternalLink className="w-3 h-3 mr-1" /> Open thread
                </Button>
                {flag.status === 'open' && (
                  <>
                    <Button size="sm" variant="outline" className="rounded-full"
                      onClick={() => resolve(flag, 'dismiss')}
                      data-testid={`flag-dismiss-${flag.id}`}
                    >
                      <X className="w-3 h-3 mr-1" /> Dismiss (post is fine)
                    </Button>
                    <Button size="sm" className="rounded-full font-bold"
                      onClick={() => resolve(flag, 'actioned')}
                      data-testid={`flag-action-${flag.id}`}
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Mark as actioned
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
